/**
 * Preset layout logbook (solo frontend): metriche, priorità A–E, util per split preset D.
 * Costanti tunabili in cima.
 */

export const STRAPI_COMPONENT = '__component';
export const EMBED_COMPONENT = 'logbook.embed-block';
export const GALLERY_BLOCK_COMPONENT = 'logbook.gallery-block';

/** Minimo embed per preset D. */
export const EMBED_HEAVY_MIN = 2;
/** Preset E: articolo lungo. */
export const LONG_BODY_CHARS = 3500;
export const LONG_BODY_PARAGRAPHS = 12;
/** Preset C: molte immagini totali. */
export const MEDIA_RICH_MIN_IMAGES = 3;
/** Preset C: almeno un gallery-block con N immagini. */
export const MEDIA_RICH_GALLERY_BLOCK_MIN = 3;
/** Preset D: body abbastanza lungo per split intro + fascia Social. */
export const D_LONG_BODY_CHARS = 900;

const stableSectionKey = (prefix, idx, extra = '') =>
  `${prefix}-${idx}-${String(extra).slice(0, 40)}`;

/** @param {unknown} block */
const blockPlainTextLen = (block) => {
  if (!block || typeof block !== 'object') return 0;
  const walk = (node) => {
    if (!node) return 0;
    if (typeof node.text === 'string') return node.text.length;
    if (Array.isArray(node.children))
      return node.children.reduce((acc, ch) => acc + walk(ch), 0);
    return 0;
  };
  if (block.type === 'paragraph' || block.type === 'heading')
    return walk({ children: block.children });
  if (block.type === 'list') {
    return (block.children || []).reduce(
      (a, li) => a + walk({ children: li.children }),
      0
    );
  }
  if (block.type === 'quote') return walk({ children: block.children });
  return 0;
};

/** @param {unknown[]} body */
export const getBodyPlainLen = (body) => {
  const blocks = Array.isArray(body) ? body : [];
  return blocks.reduce((sum, b) => sum + blockPlainTextLen(b), 0);
};

/** @param {unknown[]} body */
export const countBodyParagraphs = (body) => {
  const blocks = Array.isArray(body) ? body : [];
  return blocks.filter((b) => b && b.type === 'paragraph').length;
};

/** @param {unknown[]} contentBlocks */
export const getEmbedCount = (contentBlocks) => {
  const list = Array.isArray(contentBlocks) ? contentBlocks : [];
  return list.filter((b) => b && b[STRAPI_COMPONENT] === EMBED_COMPONENT)
    .length;
};

/** @param {{ gallery?: unknown[]; contentBlocks?: unknown[] }} entry */
export const getGalleryImageCount = (entry) => {
  let n = Array.isArray(entry?.gallery) ? entry.gallery.length : 0;
  const blocks = Array.isArray(entry?.contentBlocks) ? entry.contentBlocks : [];
  blocks.forEach((b) => {
    if (b && b[STRAPI_COMPONENT] === GALLERY_BLOCK_COMPONENT) {
      n += Array.isArray(b.images) ? b.images.length : 0;
    }
  });
  return n;
};

/** @param {{ contentBlocks?: unknown[] }} entry */
export const hasGalleryBlockWithMinImages = (entry, min) => {
  const blocks = Array.isArray(entry?.contentBlocks) ? entry.contentBlocks : [];
  return blocks.some(
    (b) =>
      b &&
      b[STRAPI_COMPONENT] === GALLERY_BLOCK_COMPONENT &&
      Array.isArray(b.images) &&
      b.images.length >= min
  );
};

/**
 * @param {{ heroImage?: { url?: string }; body?: unknown[]; contentBlocks?: unknown[]; gallery?: unknown[] }} entry
 */
export const getLogbookLayoutMetrics = (entry) => {
  const body = entry?.body;
  return {
    hasHero: Boolean(entry?.heroImage?.url),
    embedCount: getEmbedCount(entry?.contentBlocks),
    galleryImageCount: getGalleryImageCount(entry),
    bodyPlainLen: getBodyPlainLen(body),
    bodyParagraphs: countBodyParagraphs(body),
  };
};

/**
 * @param {{ heroImage?: { url?: string }; body?: unknown[]; contentBlocks?: unknown[]; gallery?: unknown[] }} entry
 * @returns {'A'|'B'|'C'|'D'|'E'}
 */
export const resolveLogbookLayout = (entry) => {
  const m = getLogbookLayoutMetrics(entry);
  if (m.embedCount >= EMBED_HEAVY_MIN) return 'D';
  if (
    m.bodyPlainLen >= LONG_BODY_CHARS ||
    m.bodyParagraphs >= LONG_BODY_PARAGRAPHS
  )
    return 'E';
  if (
    m.galleryImageCount >= MEDIA_RICH_MIN_IMAGES ||
    hasGalleryBlockWithMinImages(entry, MEDIA_RICH_GALLERY_BLOCK_MIN)
  )
    return 'C';
  if (m.hasHero) return 'B';
  return 'A';
};

/**
 * Spezza i blocks del body dopo i primi 2 blocchi `paragraph`.
 * @param {unknown[]} body
 * @returns {{ intro: unknown[]; rest: unknown[] }}
 */
export const splitBodyAfterTwoParagraphs = (body) => {
  const blocks = Array.isArray(body) ? body : [];
  let paraCount = 0;
  let splitAt = blocks.length;
  let firstParaEnd = -1;
  for (let i = 0; i < blocks.length; i += 1) {
    if (blocks[i]?.type === 'paragraph') {
      paraCount += 1;
      if (paraCount === 1) firstParaEnd = i + 1;
      if (paraCount >= 2) {
        splitAt = i + 1;
        break;
      }
    }
  }
  if (paraCount === 1 && firstParaEnd > 0) {
    splitAt = firstParaEnd;
  }
  return { intro: blocks.slice(0, splitAt), rest: blocks.slice(splitAt) };
};

/**
 * Sezioni galleria unificata: prima immagini top-level, poi per ogni gallery-block con immagini.
 * @param {{ gallery?: unknown[]; contentBlocks?: unknown[] }} entry
 * @returns {{ key: string; heading: string | null; images: unknown[] }[]}
 */
export const buildUnifiedGallerySections = (entry) => {
  const sections = [];
  let idx = 0;
  if (Array.isArray(entry?.gallery) && entry.gallery.length) {
    sections.push({
      key: stableSectionKey('gal-top', idx, ''),
      heading: null,
      images: entry.gallery,
    });
    idx += 1;
  }
  const blocks = Array.isArray(entry?.contentBlocks) ? entry.contentBlocks : [];
  blocks.forEach((b, i) => {
    if (
      b?.[STRAPI_COMPONENT] === GALLERY_BLOCK_COMPONENT &&
      Array.isArray(b.images) &&
      b.images.length
    ) {
      sections.push({
        key: stableSectionKey('gal-block', idx, i),
        heading:
          typeof b.heading === 'string' && b.heading.trim()
            ? b.heading.trim()
            : null,
        images: b.images,
      });
      idx += 1;
    }
  });
  return sections;
};

/** @param {unknown[]} contentBlocks */
export const extractEmbedBlocks = (contentBlocks) => {
  const list = Array.isArray(contentBlocks) ? contentBlocks : [];
  return list.filter((b) => b && b[STRAPI_COMPONENT] === EMBED_COMPONENT);
};

/**
 * @param {unknown[]} contentBlocks
 * @param {{ stripEmbed?: boolean; stripGalleryBlocks?: boolean }} opts
 */
export const filterContentBlocksForFlow = (contentBlocks, opts = {}) => {
  const { stripEmbed = false, stripGalleryBlocks = false } = opts;
  const list = Array.isArray(contentBlocks) ? contentBlocks : [];
  return list.filter((b) => {
    const c = b?.[STRAPI_COMPONENT];
    if (stripEmbed && c === EMBED_COMPONENT) return false;
    if (stripGalleryBlocks && c === GALLERY_BLOCK_COMPONENT) return false;
    return true;
  });
};

/** @param {unknown} entry */
export const shouldSplitBodyForLayoutD = (entry) => {
  if (resolveLogbookLayout(entry) !== 'D') return false;
  return getBodyPlainLen(entry?.body) >= D_LONG_BODY_CHARS;
};
